import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
} from '@react-pdf/renderer'
import type { CV, DateFormat, CVLanguage } from '../../types/cv'
import { modernPalettes, type ModernColors } from '../../utils/colorPalettes'
import { getCVLabels, getLevelLabel } from '../../utils/cvTranslations'
import { EmailIcon, PhoneIcon, LocationIcon, BirthDateIcon } from './ContactIcons'
import { DEFAULT_AVATAR } from '../../assets/defaultAvatar'


function getColors(paletteId?: string): ModernColors {
  return modernPalettes[paletteId || 'gold'] || modernPalettes.gold
}

// Colors matching the design
const DARK_GRAY = '#3D3D3D'
const LIGHT_BG = '#F5F5F5'
const TEXT_GRAY = '#666666'

const styles = StyleSheet.create({
  page: {
    fontFamily: 'Helvetica',
    fontSize: 9,
    lineHeight: 1.5,
    backgroundColor: '#FFFFFF',
    paddingTop: 25,
  },
  // Header area - negative margin to reach page edge despite paddingTop
  headerContainer: {
    flexDirection: 'row',
    height: 180,
    position: 'relative',
    marginTop: -25,
  },
  // Left side - dark with photo
  headerLeft: {
    width: '45%',
    backgroundColor: DARK_GRAY,
    position: 'relative',
  },
  headerPhoto: {
    position: 'absolute',
    top: 20,
    left: 30,
    width: 220,
    height: 220,
    objectFit: 'cover',
    borderRadius: 4,
  },
  // Right side - light with contact
  headerRight: {
    width: '55%',
    backgroundColor: LIGHT_BG,
    padding: 25,
    paddingTop: 20,
    position: 'relative',
  },
  // Yellow accent bar top-right
  accentBar: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: 8,
    height: 60,
  },
  // Name section in header right
  nameSection: {
    marginBottom: 15,
    marginTop: 10,
  },
  nameFirst: {
    fontSize: 22,
    color: DARK_GRAY,
    fontWeight: 400,
    letterSpacing: 2,
  },
  nameLast: {
    fontSize: 22,
    color: DARK_GRAY,
    fontWeight: 700,
    letterSpacing: 2,
  },
  title: {
    fontSize: 10,
    letterSpacing: 3,
    marginTop: 4,
    textTransform: 'uppercase',
  },
  // Contact items
  contactList: {
    marginTop: 15,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
    textDecoration: 'none',
  },
  contactDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginRight: 10,
  },
  contactIcon: {
    fontSize: 10,
    marginRight: 8,
  },
  contactText: {
    fontSize: 9,
    color: DARK_GRAY,
  },
  // Main content - two columns
  mainContent: {
    flexDirection: 'row',
    flex: 1,
    paddingTop: 25,
  },
  // Left column - extra top padding to clear the overflowing photo
  leftColumn: {
    width: '50%',
    paddingLeft: 30,
    paddingRight: 20,
    paddingTop: 50,
  },
  // Right column
  rightColumn: {
    width: '50%',
    paddingLeft: 20,
    paddingRight: 30,
  },
  // Section styles
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: 2,
    textTransform: 'uppercase',
    color: DARK_GRAY,
    marginBottom: 3,
  },
  sectionUnderline: {
    width: 25,
    height: 2,
    marginBottom: 12,
  },
  // Entry styles (for education/experience)
  entryContainer: {
    marginBottom: 12,
  },
  entryTitle: {
    fontSize: 10,
    fontWeight: 700,
    color: DARK_GRAY,
  },
  entrySubtitle: {
    fontSize: 9,
    marginTop: 1,
  },
  entryDescription: {
    fontSize: 8,
    color: TEXT_GRAY,
    marginTop: 4,
    lineHeight: 1.6,
  },
  entryDate: {
    fontSize: 8,
    color: TEXT_GRAY,
    marginTop: 2,
  },
  // Profile/Summary
  summaryText: {
    fontSize: 9,
    color: TEXT_GRAY,
    lineHeight: 1.7,
  },
  // Skills - two column layout
  skillsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  skillItem: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
    width: '50%',
  },
  skillBullet: {
    width: 4,
    height: 4,
    borderRadius: 2,
    marginRight: 6,
  },
  skillName: {
    fontSize: 8,
    color: DARK_GRAY,
  },
  // Languages
  languageItem: {
    marginBottom: 8,
  },
  languageName: {
    fontSize: 9,
    fontWeight: 700,
    color: DARK_GRAY,
    textTransform: 'uppercase',
    letterSpacing: 1,
  },
  languageLevel: {
    fontSize: 8,
    color: TEXT_GRAY,
    marginTop: 2,
  },
  // Links
  linkItem: {
    marginBottom: 4,
  },
  linkText: {
    fontSize: 8,
    color: TEXT_GRAY,
  },
})

const localeMap: Record<CVLanguage, string> = {
  es: 'es-ES',
  en: 'en-US',
  fr: 'fr-FR',
  pt: 'pt-PT',
  de: 'de-DE',
}

function formatDate(
  dateStr: string | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  if (!dateStr) return ''
  const str = dateStr.trim().toLowerCase()
  const locale = localeMap[language] || 'en-US'
  if (str.includes('present') || str.includes('actual') || str.includes('current')) {
    return presentLabel
  }
  const yearOnly = format === 'year-only'
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-')
    if (yearOnly) return year
    const date = new Date(parseInt(year), parseInt(month) - 1)
    if (!isNaN(date.getTime())) {
      const formatted = date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    }
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    if (yearOnly) return dateStr.split('-')[0]
    const date = new Date(dateStr)
    if (!isNaN(date.getTime())) {
      const formatted = date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    }
  }
  if (/^\d{4}$/.test(dateStr)) {
    return dateStr
  }
  const mmYYYYMatch = dateStr.match(/^(\d{1,2})[/-](\d{4})$/)
  if (mmYYYYMatch) {
    if (yearOnly) return mmYYYYMatch[2]
    const date = new Date(parseInt(mmYYYYMatch[2]), parseInt(mmYYYYMatch[1]) - 1)
    if (!isNaN(date.getTime())) {
      const formatted = date.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
      return formatted.charAt(0).toUpperCase() + formatted.slice(1)
    }
  }
  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/)
  if (yearMatch) {
    return yearMatch[0]
  }
  return ''
}

function formatDateRange(
  start: string | undefined,
  end: string | undefined,
  current: boolean | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  const startStr = formatDate(start, format, language, presentLabel)
  const endStr = current ? presentLabel : formatDate(end, format, language, presentLabel)
  if (!startStr && !endStr) return ''
  if (!startStr) return endStr
  if (!endStr) return startStr
  return `${startStr} — ${endStr}`
}


// Split name into first and last
function splitName(fullName: string): { first: string; last: string } {
  const parts = fullName.trim().split(' ')
  if (parts.length === 1) {
    return { first: parts[0], last: '' }
  }
  const last = parts.pop() || ''
  const first = parts.join(' ')
  return { first, last }
}

interface ModernTemplateProps {
  cv: CV
  colorPalette?: string
}


export function ModernTemplate({ cv, colorPalette }: ModernTemplateProps) {
  const colors = getColors(colorPalette)
  const labels = getCVLabels(cv.settings?.language, cv.settings?.customLabels)
  const showIcons = cv.settings?.showContactIcons ?? true

  const hasPersonalInfo =
    cv.personalInfo?.fullName ||
    cv.personalInfo?.title ||
    cv.personalInfo?.email ||
    cv.personalInfo?.phone ||
    cv.personalInfo?.location

  const hasExperience = cv.experience && cv.experience.length > 0
  const hasEducation = cv.education && cv.education.length > 0
  const allSkills = [
    ...(cv.skills?.technical || []),
    ...(cv.skills?.soft || []),
    ...(cv.skills?.tools || []),
  ]
  const hasSkills = allSkills.length > 0
  const hasLanguages = cv.languages && cv.languages.some((lang) => lang.name)

  const nameParts = cv.personalInfo?.fullName ? splitName(cv.personalInfo.fullName) : { first: '', last: '' }

  return (
    <Document
      title={`CV - ${cv.personalInfo?.fullName || 'Curriculum Vitae'}`}
      author={cv.personalInfo?.fullName || ''}
      subject={cv.personalInfo?.title || 'Curriculum Vitae'}
      creator="CVtoday"
      keywords={`CV, resume, ${cv.personalInfo?.fullName || ''}, ${cv.personalInfo?.title || ''}`}
    >
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.headerContainer}>
          {/* Left - Dark with photo */}
          <View style={[styles.headerLeft, { backgroundColor: colors.dark }]}>
            <Image src={cv.personalInfo?.photo || DEFAULT_AVATAR} style={styles.headerPhoto} />
          </View>

          {/* Right - Light with name and contact */}
          <View style={styles.headerRight}>
            {/* Yellow accent bar */}
            <View style={[styles.accentBar, { backgroundColor: colors.primary }]} />

            {/* Name */}
            {hasPersonalInfo && (
              <View style={styles.nameSection}>
                <Text>
                  <Text style={styles.nameFirst}>{nameParts.first} </Text>
                  <Text style={styles.nameLast}>{nameParts.last}</Text>
                </Text>
                {cv.personalInfo?.title && (
                  <Text style={[styles.title, { color: colors.primary }]}>
                    {cv.personalInfo.title.toUpperCase()}
                  </Text>
                )}
              </View>
            )}

            {/* Contact info */}
            <View style={styles.contactList}>
              {cv.personalInfo?.links && cv.personalInfo.links.length > 0 && (
                cv.personalInfo.links.map((link) =>
                  link.url ? (
                    <Link key={link.id} src={link.url} style={styles.contactItem}>
                      <View style={[styles.contactDot, { backgroundColor: colors.primary }]} />
                      <Text style={styles.contactText}>{link.label || link.url}</Text>
                    </Link>
                  ) : null
                )
              )}
              {cv.personalInfo?.email && (
                <Link src={`mailto:${cv.personalInfo.email}`} style={styles.contactItem}>
                  {showIcons ? (
                    <EmailIcon size={11} color={colors.primary} style={{ marginRight: 8, marginBottom: 1 }} />
                  ) : (
                    <View style={[styles.contactDot, { backgroundColor: colors.primary }]} />
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.email}</Text>
                </Link>
              )}
              {cv.personalInfo?.phone && (
                <Link src={`tel:${cv.personalInfo.phone}`} style={styles.contactItem}>
                  {showIcons ? (
                    <PhoneIcon size={11} color={colors.primary} style={{ marginRight: 8, marginBottom: 1 }} />
                  ) : (
                    <View style={[styles.contactDot, { backgroundColor: colors.primary }]} />
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.phone}</Text>
                </Link>
              )}
              {cv.personalInfo?.location && (
                <View style={styles.contactItem}>
                  {showIcons ? (
                    <LocationIcon size={11} color={colors.primary} style={{ marginRight: 8, marginBottom: 1 }} />
                  ) : (
                    <View style={[styles.contactDot, { backgroundColor: colors.primary }]} />
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.location}</Text>
                </View>
              )}
              {cv.personalInfo?.birthDate && (
                <View style={styles.contactItem}>
                  {showIcons ? (
                    <BirthDateIcon size={11} color={colors.primary} style={{ marginRight: 8, marginBottom: 1 }} />
                  ) : (
                    <View style={[styles.contactDot, { backgroundColor: colors.primary }]} />
                  )}
                  <Text style={styles.contactText}>{cv.personalInfo.birthDate}</Text>
                </View>
              )}
            </View>
          </View>
        </View>

        {/* Main Content - Two Columns */}
        <View style={styles.mainContent}>
          {/* Left Column - Summary & Experience */}
          <View style={styles.leftColumn}>
            {/* Summary - placed here for better ATS reading order */}
            {cv.summary && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{labels.summary}</Text>
                <View style={[styles.sectionUnderline, { backgroundColor: colors.primary }]} />
                <Text style={styles.summaryText}>{cv.summary}</Text>
              </View>
            )}

            {/* Experience */}
            {hasExperience && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle} minPresenceAhead={40}>{labels.experience}</Text>
                <View style={[styles.sectionUnderline, { backgroundColor: colors.primary }]} />
                {cv.experience?.map((exp) => (
                  <View key={exp.id} style={styles.entryContainer}>
                    <Text style={styles.entryTitle}>{exp.company || 'Company'}</Text>
                    <Text style={[styles.entrySubtitle, { color: colors.primary }]}>
                      {exp.position}
                    </Text>
                    {(exp.startDate || exp.endDate) && (
                      <Text style={styles.entryDate}>
                        {formatDateRange(exp.startDate, exp.endDate, exp.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                      </Text>
                    )}
                    {exp.description && (
                      <Text style={styles.entryDescription}>{exp.description}</Text>
                    )}
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Right Column - Education, Skills, Languages */}
          <View style={styles.rightColumn}>
            {/* Education */}
            {hasEducation && (
              <View style={styles.section}>
                <Text style={styles.sectionTitle}>{labels.education}</Text>
                <View style={[styles.sectionUnderline, { backgroundColor: colors.primary }]} />
                {cv.education?.map((edu) => (
                  <View key={edu.id} style={styles.entryContainer}>
                    <Text style={styles.entryTitle}>{edu.institution || 'Institution'}</Text>
                    <Text style={[styles.entrySubtitle, { color: colors.primary }]}>
                      {edu.degree}{edu.field ? ` - ${edu.field}` : ''}
                    </Text>
                    {(edu.startDate || edu.endDate) && (
                      <Text style={styles.entryDate}>
                        {formatDateRange(edu.startDate, edu.endDate, edu.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                      </Text>
                    )}
                    {edu.gpa && (
                      <Text style={styles.entryDescription}>{labels.gpa}: {edu.gpa}</Text>
                    )}
                  </View>
                ))}
              </View>
            )}

            {/* Skills */}
            {hasSkills && (
              <View style={styles.section} wrap={false}>
                <Text style={styles.sectionTitle}>{labels.skills}</Text>
                <View style={[styles.sectionUnderline, { backgroundColor: colors.primary }]} />
                <View style={styles.skillsGrid}>
                  {allSkills.map((skill, index) => (
                    <View key={index} style={styles.skillItem}>
                      <View style={[styles.skillBullet, { backgroundColor: colors.primary }]} />
                      <Text style={styles.skillName}>{skill}</Text>
                    </View>
                  ))}
                </View>
              </View>
            )}

            {/* Languages */}
            {hasLanguages && (
              <View style={styles.section} wrap={false}>
                <Text style={styles.sectionTitle}>{labels.languages}</Text>
                <View style={[styles.sectionUnderline, { backgroundColor: colors.primary }]} />
                {cv.languages?.map((lang) =>
                  lang.name ? (
                    <View key={lang.id} style={styles.languageItem}>
                      <Text style={styles.languageName}>{lang.name}</Text>
                      {lang.level && (
                        <Text style={styles.languageLevel}>{getLevelLabel(lang.level, cv.settings?.language)}</Text>
                      )}
                    </View>
                  ) : null
                )}
              </View>
            )}
          </View>
        </View>
      </Page>
    </Document>
  )
}
